import { observer } from "mobx-react-lite";
import type { FC } from "react";
import type { EdgeProps } from "@xyflow/react";
import { useReactFlow } from "@xyflow/react";
import { GrayColors, SolutionColorVariables } from "@smartsuite/types";
import { useRootStore } from "../../../stores/hooks";
import { useFlowCanvasHandlers } from "../nodes/context";
import type { FlowNodeData } from "../graph/types";
import type { GroupLocator } from "../../../stores/automation/utils/action-group-walk";
import { parseActionHash, stepEdgeHighlight } from "../utils/action-highlight";
import { buildStepEdgePath } from "./step-edge-path";
import { MIN_STEP_CORNER_SPAN } from "../graph/constants";

const spineStroke = (isHighlighted: boolean): string =>
  isHighlighted ? SolutionColorVariables.sc1 : GrayColors["G-2"];

const stubStroke = (isActive: boolean, isHighlighted: boolean): string =>
  isActive
    ? SolutionColorVariables.sc1
    : isHighlighted
      ? SolutionColorVariables.sc1
      : GrayColors["G-2"];

const locatorFromActionData = (
  data: FlowNodeData | undefined,
): GroupLocator | undefined => {
  if (data?.kind === "action") return data.locator;
  return undefined;
};

/** Solid L-shaped connector between stacked cards — replica of the legacy
 *  StyledLine (Sidebar/style.tsx) with cascade + active stroke colours. */
export const StepEdge: FC<EdgeProps> = observer(
  ({ id, source, target, sourceX, sourceY, targetX, targetY }) => {
    const { getNode } = useReactFlow();
    const store = useRootStore();
    const handlers = useFlowCanvasHandlers();
    const candidate = store.automations.candidate;

    const targetNode = getNode(target);
    const targetData = targetNode?.data as FlowNodeData | undefined;
    const targetActionHash = parseActionHash(target);
    const targetLocator = locatorFromActionData(targetData);

    const { isHighlighted, isActive } = stepEdgeHighlight(
      candidate,
      handlers.activeAction,
      handlers.activeItem?.type === "trigger",
      source,
      target,
      targetLocator,
      targetActionHash,
    );

    const span = targetY - sourceY;
    const { spinePath, stubPath, straightPath } = buildStepEdgePath(
      sourceX,
      sourceY,
      targetX,
      targetY,
    );

    // Stacked cards are CARD_VGAP apart — inter-node edges only draw ticks in
    // the gutter. Per-card rails live on ActionNode (legacy StyledLine).
    if (straightPath === null && span < MIN_STEP_CORNER_SPAN) {
      return null;
    }

    if (straightPath !== null) {
      return (
        <path
          id={id}
          d={straightPath}
          fill="none"
          stroke={spineStroke(isHighlighted || isActive)}
          strokeWidth={1}
        />
      );
    }

    return (
      <g id={id}>
        <path
          d={spinePath}
          fill="none"
          stroke={spineStroke(isHighlighted)}
          strokeWidth={1}
        />
        {stubPath !== null && (
          <path
            d={stubPath}
            fill="none"
            stroke={stubStroke(isActive, isHighlighted)}
            strokeWidth={1}
          />
        )}
      </g>
    );
  },
);
StepEdge.displayName = "StepEdge";
